/**
 * M7 S33 story seeds (spec §12.4) as the station screens show them. Every word comes from data (`stations.json`
 * `desk`, `lease.json office`, `career.json ship`), so the naming pass is a data edit; a missing seed reads as null
 * and the screen shows nothing.
 *
 * - **Desks (C8):** Duarte at the Lease Office and Hale at the claim desk, named on the header; Hale's stamp on claim
 *   registrations (`claimStamp`); Hale's thank-you after a clear-raiders hand-in (`raidersThanks`).
 * - **Paper (PC-8):** Voss's CEO signature on Refinery receipts (`receiptLine`) and TCF statements (the lease model).
 * - **The board (§8.2):** the Meridian contracts desk line (`boardLine`).
 * - **The ship (PC-1):** the Kestrel's frame serial and registration (`frameLabel`).
 * - **FR stations (texture):** the bay plate in words (`plate`); the marks themselves are decal ids for the art lane.
 */
import type { ContractType, M2Data, StationDesk, StationId } from '../../data/m2Schemas'
import { stationDef, stationIndexOf } from '../../data/m2'

export interface SeedPerson {
  name: string
  role: string
}

export interface SeedLine {
  speaker: string
  text: string
}

/** The desk seeds of `station`, or null when it has none (or the id is unknown). */
export function deskOf(data: Pick<M2Data, 'stations'>, station: StationId): StationDesk | null {
  if (stationIndexOf(data, station) < 0) return null
  return stationDef(data, station).desk ?? null
}

/** "Signed: Maren Voss, Chief Executive". */
export function signatureLine(p: SeedPerson): string {
  return `Signed: ${p.name}, ${p.role}`
}

/** "Lease Office: Ines Duarte, registry clerk · Claim desk: Commissioner Hale, Charter Office", or null. */
export function deskStaffLine(desk: StationDesk | null): string | null {
  if (desk === null) return null
  const parts: string[] = []
  if (desk.leaseClerk !== undefined) parts.push(`Lease Office: ${desk.leaseClerk.name}, ${desk.leaseClerk.role.toLowerCase()}`)
  if (desk.claimOfficer !== undefined) parts.push(`Claim desk: ${desk.claimOfficer.name}, ${desk.claimOfficer.role}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** The receipt footer of a market sale here ("Tethys Mining Combine · Refinery receipt · Signed: …"), or null. */
export function receiptLine(desk: StationDesk | null): string | null {
  const r = desk?.receipt
  return r === undefined ? null : `${r.issuer} · ${signatureLine(r.signedBy)}`
}

/** The stamp on a claim registration: the first station desk that carries one (the claim desk), or null. */
export function claimStamp(data: Pick<M2Data, 'stations'>): string | null {
  for (const s of data.stations.stations) if (s.desk?.claimStamp !== undefined) return s.desk.claimStamp
  return null
}

/** The thank-you after a hand-in of `type` at `station`: only a clear-raiders job at a desk that has one. */
export function raidersThanks(data: Pick<M2Data, 'stations'>, station: StationId, type: ContractType): SeedLine | null {
  if (type !== 'clearRaiders') return null
  return deskOf(data, station)?.raidersThanks ?? null
}

/** "KT-3 frame T-0388 · registration RG-2291-K" (the ship screen), or null without the seed. */
export function frameLabel(data: Pick<M2Data, 'career'>): string | null {
  const s = data.career.ship
  return s === undefined ? null : `${s.frame} frame ${s.serial} · registration ${s.registration}`
}
